import { Bookmark } from "@novorender/data-js-api";
import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useSelectBookmark } from "features/bookmarks/useSelectBookmark";
import { explorerActions, selectLocalBookmarkId, selectUrlBookmarkId } from "slices/explorer";
import { telemetry } from "telemetry";

import { selectAutofreezeEnabled } from "../renderSlice";

export function useHandleInitialBookmark() {
    const {
        state: { view },
    } = useExplorerGlobals();

    const urlBookmarkId = useAppSelector(selectUrlBookmarkId);
    const localBookmarkId = useAppSelector(selectLocalBookmarkId);
    const selectBookmark = useSelectBookmark();
    const dispatch = useAppDispatch();
    const autofreezeEnabled = useAppSelector(selectAutofreezeEnabled);
    const autofreezeEnabledRef = useRef(autofreezeEnabled);
    autofreezeEnabledRef.current = autofreezeEnabled;

    useEffect(() => {
        if (!view || !urlBookmarkId) {
            return;
        }

        dispatch(explorerActions.setUrlBookmarkId(undefined));
        selectBookmark(
            { kind: "deepLink", bookmarkId: urlBookmarkId },
            { autofreezeEnabled: autofreezeEnabledRef.current },
        );
    }, [view, dispatch, selectBookmark, urlBookmarkId]);

    useEffect(() => {
        if (!view || !localBookmarkId) {
            return;
        }

        dispatch(explorerActions.setLocalBookmarkId(undefined));
        const bookmark = readLocalBookmark(localBookmarkId);

        if (bookmark) {
            selectBookmark({ kind: "state", bookmark }, { autofreezeEnabled: autofreezeEnabledRef.current });
        }
    }, [view, dispatch, selectBookmark, localBookmarkId]);
}

/** Reads and clears the bookmark stashed in session storage before a reload or an OAuth redirect. */
function readLocalBookmark(id: string): Bookmark | undefined {
    try {
        const stored = sessionStorage.getItem(id);

        if (!stored) {
            return undefined;
        }

        sessionStorage.removeItem(id);
        return (JSON.parse(stored) as Bookmark | null) ?? undefined;
    } catch (error) {
        telemetry.trackCaughtException({
            source: "render.useHandleInitialBookmark.readLocalBookmark",
            category: "init",
            error,
        });
        return undefined;
    }
}
